import React, { Component } from 'react'
import {
    Card, Table, Button, Drawer, Form, Input, Select, Space, message,
    Popconfirm, Typography, Row, Col, Tag, Spin, Modal, Alert, InputNumber, Switch
} from 'antd'
import {
    PlusOutlined, EditOutlined, DeleteOutlined, RocketOutlined, ReloadOutlined,
    ThunderboltOutlined, CheckCircleOutlined, StopOutlined, CodeOutlined,
    BugOutlined, CopyOutlined
} from '@ant-design/icons'
import {
    getMcpToolList, saveMcpTool, updateMcpTool,
    saveMcpToolVersion, publishMcpToolVersion, getMcpToolVersionList,
    saveApiRegistry, updateApiRegistry, getApiRegistryList, createToolCombo,
    testMcpToolVersion, validateMcpToolVersion, getMcpServiceList, getMcpToolPublishedSpec
} from '../../request'

const { Text } = Typography
const { TextArea } = Input

// 极大简化的极客一站式工作台
export default class McpTool extends Component {
    formRef = React.createRef()
    handledEntrySearch = ''

    state = {
        dataSource: [],
        apiDataSource: [],
        loading: false,
        saving: false,
        drawerVisible: false,
        editRecord: null,
        parsedParams: [],
        parsedOutputs: [],
        // 测试相关
        testing: false,
        testResult: null,
        testModalVisible: false,
        // 发布成功连接信息
        publishInfoVisible: false,
        publishedTool: null,
        // 服务信息
        serviceInfo: null,
        inputSchemaEmptyObject: false,
        selectedApiCode: undefined,
        apiMode: 'managed',
        formSnapshot: {},
        fixedHeaders: [],
        responseMappings: [],
        examples: [],
        specPreviewVisible: false,
        specPreviewLoading: false,
        specPreviewData: null
    }

    componentDidMount() {
        this.loadList()
        this.loadApiList()
        this.loadServiceInfo()
    }

    componentDidUpdate(prevProps, prevState) {
        const prevSearch = prevProps.location?.search || ''
        const currentSearch = this.getCurrentSearch()

        if (prevSearch !== currentSearch) {
            this.handledEntrySearch = ''
            this.loadList()
            this.loadApiList()
            this.loadServiceInfo()
            return
        }

        if (
            prevState.dataSource !== this.state.dataSource
            || prevState.apiDataSource !== this.state.apiDataSource
        ) {
            this.handleEntryQuery()
        }

        if (prevState.parsedOutputs !== this.state.parsedOutputs) {
            const nextResponseMappings = this.buildResponseMappingsFromOutputs(this.state.parsedOutputs)
            if (!this.areResponseMappingsEqual(nextResponseMappings, this.state.responseMappings)) {
                this.setState({ responseMappings: nextResponseMappings })
            }
        }
    }

    getCurrentSearch = () => this.props.location?.search || window.location.search || ''

    getQueryParams = () => {
        const params = new URLSearchParams(this.getCurrentSearch())
        const serviceId = params.get('serviceId')
        const toolId = params.get('toolId')
        const apiCode = params.get('apiCode')
        return {
            serviceId: serviceId ? Number(serviceId) : undefined,
            toolId: toolId ? Number(toolId) : undefined,
            apiCode: apiCode || undefined,
        }
    }

    getServiceId = () => {
        return this.getQueryParams().serviceId
    }

    handleEntryQuery = () => {
        const currentSearch = this.getCurrentSearch()
        if (!currentSearch || this.handledEntrySearch === currentSearch) return

        const { toolId, apiCode } = this.getQueryParams()

        if (toolId) {
            const targetTool = this.state.dataSource.find(item => String(item.id) === String(toolId))
            if (!targetTool) return
            this.handledEntrySearch = currentSearch
            this.openEdit(targetTool)
            return
        }

        if (apiCode) {
            const targetApi = this.state.apiDataSource.find(item => item.apiCode === apiCode)
            if (!targetApi) return
            this.handledEntrySearch = currentSearch
            this.openCreate(() => this.handleApiSelect(apiCode))
        }
    }

    loadList = () => {
        this.setState({ loading: true })
        const serviceId = this.getServiceId()
        getMcpToolList(serviceId ? { serviceId } : {})
            .then(res => this.setState({ dataSource: Array.isArray(res) ? res : (res?.list || []) }))
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    loadApiList = () => {
        getApiRegistryList({})
            .then(res => this.setState({ apiDataSource: Array.isArray(res) ? res : (res?.list || []) }))
            .catch(() => {})
    }

    loadServiceInfo = () => {
        const serviceId = this.getServiceId()
        if (!serviceId) return
        getMcpServiceList({}).then(res => {
            const list = Array.isArray(res) ? res : (res?.list || [])
            const service = list.find(s => s.id === serviceId)
            if (service) this.setState({ serviceInfo: service })
        }).catch(() => {})
    }

    normalizeVersionList = (res) => {
        if (Array.isArray(res)) return res
        if (Array.isArray(res?.list)) return res.list
        if (Array.isArray(res?.records)) return res.records
        if (Array.isArray(res?.data)) return res.data
        if (Array.isArray(res?.data?.list)) return res.data.list
        if (Array.isArray(res?.data?.records)) return res.data.records
        return []
    }

    openCreate = (afterOpen) => {
        const serviceId = this.getServiceId()
        if (!serviceId) {
            message.warning('请先从「MCP服务管理」进入具体服务后再创建工具，否则工具无法归属到任何服务')
        }
        this.setState({
            drawerVisible: true,
            editRecord: null,
            parsedParams: [],
            parsedOutputs: [],
            inputSchemaEmptyObject: false,
            selectedApiCode: undefined,
            apiMode: 'managed',
            formSnapshot: { method: 'POST', category: '其他', apiMode: 'managed', authRequired: false, userContext: false, maxRows: 100 },
            fixedHeaders: [],
            responseMappings: [],
            examples: [],
            specPreviewData: null
        })
        setTimeout(() => {
            if (this.formRef.current) {
                this.formRef.current.resetFields()
                this.formRef.current.setFieldsValue({
                    method: 'POST', category: '其他', apiMode: 'managed', authRequired: false, userContext: false, maxRows: 100
                })
            }
            if (typeof afterOpen === 'function') {
                afterOpen()
            }
        }, 0)
    }

    openEdit = async (record) => {
        const initialApiMode = record.apiCode ? 'bind_existing' : 'managed'
        this.setState({
            drawerVisible: true,
            editRecord: record,
            parsedParams: [],
            parsedOutputs: [],
            inputSchemaEmptyObject: false,
            selectedApiCode: record.apiCode || undefined,
            apiMode: initialApiMode,
            fixedHeaders: [],
            responseMappings: [],
            examples: [],
            specPreviewData: null
        })

        // 我们尝试拉取最新的 Version，然后还原表单
        try {
            const versionRes = await getMcpToolVersionList({ toolId: record.id })
            const versions = this.normalizeVersionList(versionRes)
            const activeVersion = versions && versions.length > 0 ? versions[0] : null

            // 匹配 API 信息
            const matchedApi = this.state.apiDataSource.find(api => api.apiCode === record.apiCode)

            const parsedConfig = this.safeParseJson(activeVersion?.configJson, 'Failed to parse configJson in edit mode')
            const policy = activeVersion?.policyJson ? this.safeParseJson(activeVersion.policyJson) : (parsedConfig?.policy || null)
            const fixedHeaders = Array.isArray(policy?.fixedHeaders) ? policy.fixedHeaders : []
            const responseMapping = this.safeParseJson(activeVersion?.responseMapping) || parsedConfig?.binding?.responseMapping || {}
            const responseMappings = Object.keys(responseMapping || {}).map(key => ({ fieldName: key, jsonPath: responseMapping[key] }))
            const examples = Array.isArray(activeVersion?.examples) ? activeVersion.examples.map(item => ({
                exampleName: item?.exampleName || item?.name || '',
                userQuery: item?.userQuery || item?.query || '',
                inputJson: item?.inputJson || JSON.stringify(item?.input || {}, null, 2),
                outputJson: item?.outputJson || JSON.stringify(item?.output || {}, null, 2)
            })) : (Array.isArray(parsedConfig?.examples) ? parsedConfig.examples.map(item => ({
                exampleName: item?.name || '',
                userQuery: item?.query || '',
                inputJson: JSON.stringify(item?.input || {}, null, 2),
                outputJson: JSON.stringify(item?.output || {}, null, 2)
            })) : [])

            // 兼容不同接口返回结构：spec / configJson.tool / 顶层字段
            const specLike = activeVersion?.spec || parsedConfig || {}
            const specDescription = activeVersion?.spec?.description
                || parsedConfig?.tool?.description
                || activeVersion?.description
                || ''

            setTimeout(() => {
                if (this.formRef.current) {
                    const formValues = {
                        name: record.name || record.toolName || activeVersion?.spec?.name || parsedConfig?.tool?.name || activeVersion?.toolName || '',
                        toolCode: record.toolCode,
                        description: record.description || specDescription || '',
                        category: record.category || '其他',
                        apiMode: initialApiMode,
                        apiCode: record.apiCode || undefined,
                        method: matchedApi?.method || record.method || 'POST',
                        url: matchedApi ? ((matchedApi.baseUrl || '') + (matchedApi.path || '')) : (record.url || ''),
                        authRequired: !!policy?.authRequired,
                        userContext: !!policy?.userContext,
                        maxRows: policy?.maxRows || 100,
                    }
                    this.formRef.current.setFieldsValue(formValues)
                    this.setState({ formSnapshot: formValues, fixedHeaders, responseMappings, examples })
                }
            }, 0)

            const stateUpdates = {}

            const versionParams = Array.isArray(activeVersion?.params) ? activeVersion.params : []
            const versionOutputs = Array.isArray(activeVersion?.outputs) ? activeVersion.outputs : []
            const inputSchema = specLike?.inputSchema
            const outputSchema = specLike?.outputSchema

            const parsedInputs = this.parseParamDefinitions({
                schema: inputSchema,
                items: versionParams,
                fieldNameKey: 'paramName'
            })
            const parsedOutputs = this.parseParamDefinitions({
                schema: outputSchema,
                items: versionOutputs,
                fieldNameKey: 'fieldName',
                unwrapResponseEnvelope: true
            })

            stateUpdates.parsedParams = parsedInputs.fields
            stateUpdates.parsedOutputs = parsedOutputs.fields
            stateUpdates.inputSchemaEmptyObject = parsedInputs.isEmptyObjectSchema && parsedInputs.fields.length === 0

            if (Object.keys(stateUpdates).length > 0) {
                this.setState(stateUpdates)
            }
        } catch (error) {
            console.error(error)
        }
    }

    handleApiSelect = (apiCode) => {
        if (!apiCode) {
            this.setState({ selectedApiCode: undefined, apiMode: 'managed' })
            return
        }
        const { apiDataSource } = this.state
        const api = apiDataSource.find(a => a.apiCode === apiCode)
        if (!api) return
        this.setState({ selectedApiCode: apiCode, apiMode: 'bind_existing' })

        const parsedInputs = this.parseParamDefinitions({
            schema: api.requestSchema,
            fieldNameKey: 'paramName',
            defaultDescription: 'API带入参数'
        })
        const parsedOutputs = this.parseParamDefinitions({
            schema: api.responseSchema,
            fieldNameKey: 'fieldName',
            unwrapResponseEnvelope: true,
            defaultDescription: 'API返回字段'
        })

        const newParams = parsedInputs.fields
        const newOutputs = parsedOutputs.fields
        const jsonExampleObj = parsedInputs.exampleObject
        const jsonOutputObj = parsedOutputs.exampleObject
        const requestSchemaIsExplicitEmptyObject = parsedInputs.isEmptyObjectSchema

        const currentDesc = this.formRef.current?.getFieldValue('description') || ''
        const newDesc = api.description ? (currentDesc ? `${currentDesc}\n${api.description}` : api.description) : currentDesc

        let updates = {
            apiCode: api.apiCode,
            method: api.method || 'POST',
            url: (api.baseUrl || '') + (api.path || ''),
            description: newDesc,
        }

        const currentName = this.formRef.current?.getFieldValue('name')
        const currentToolCode = this.formRef.current?.getFieldValue('toolCode')
        if (!currentName) updates.name = api.apiName || ''
        if (!currentToolCode) updates.toolCode = api.apiCode || ''

        if (Object.keys(jsonExampleObj).length > 0) {
            updates.jsonExample = JSON.stringify(jsonExampleObj, null, 2)
        } else if (requestSchemaIsExplicitEmptyObject) {
            updates.jsonExample = JSON.stringify({}, null, 2)
        }
        if (Object.keys(jsonOutputObj).length > 0) {
            updates.jsonOutputExample = JSON.stringify(jsonOutputObj, null, 2)
        }

        if (this.formRef.current) {
            this.formRef.current.setFieldsValue(updates)
        }
        this.setState(prev => ({ formSnapshot: { ...prev.formSnapshot, ...updates } }))

        const stateUpdates = {}
        if (newParams.length > 0) stateUpdates.parsedParams = newParams
        if (newOutputs.length > 0) stateUpdates.parsedOutputs = newOutputs
        stateUpdates.inputSchemaEmptyObject = requestSchemaIsExplicitEmptyObject && newParams.length === 0
        if (Object.keys(stateUpdates).length > 0) {
            this.setState(stateUpdates)
        }

        if (newParams.length > 0 || newOutputs.length > 0) {
            message.success('已自动带入接口配置及参数模板')
        } else if (requestSchemaIsExplicitEmptyObject) {
            message.success('已自动带入接口配置，该接口无需输入参数')
        } else {
            message.success('已自动带入接口配置')
        }
    }

    handleApiModeChange = e => {
        const apiMode = typeof e === 'string' ? e : e?.target?.value
        if (!apiMode) return

        const nextSelectedApiCode = apiMode === 'bind_existing' ? this.state.selectedApiCode : undefined
        this.setState({
            apiMode,
            selectedApiCode: nextSelectedApiCode,
            inputSchemaEmptyObject: apiMode === 'bind_existing' ? this.state.inputSchemaEmptyObject : false
        })
        if (!this.formRef.current) return
        this.formRef.current.setFieldsValue({
            apiMode,
            apiCode: nextSelectedApiCode,
            jsonExample: apiMode === 'bind_existing' ? this.formRef.current.getFieldValue('jsonExample') : undefined
        })
    }

    safeParseJson = (rawValue, errorLabel) => {
        if (!rawValue) return null
        if (typeof rawValue === 'object') return rawValue
        try {
            return JSON.parse(rawValue)
        } catch (e) {
            if (errorLabel) {
                console.error(errorLabel, e)
            }
            return null
        }
    }

    buildFieldsFromSchema = (schema, fieldNameKey, unwrapResponseEnvelope = false, defaultDescription = '') => {
        return this.buildTreeFieldsFromSchema(schema, fieldNameKey, unwrapResponseEnvelope, defaultDescription)
    }

    buildJsonExampleFromSchema = (schema, unwrapResponseEnvelope = false) => {
        if (!schema) return {}
        const props = this.extractSchemaProperties(schema, unwrapResponseEnvelope)
        if (!props) return {}
        return Object.keys(props).reduce((acc, key) => {
            const fieldSchema = props[key] || {}
            const type = fieldSchema?.type || 'string'
            acc[key] = this.getPlaceholderByType(type, fieldSchema)
            return acc
        }, {})
    }

    isExplicitEmptyObjectSchema = (schema) => {
        if (!schema || typeof schema !== 'object') return false
        if (schema.type === 'object' && (!schema.properties || Object.keys(schema.properties).length === 0)) {
            return true
        }
        return Object.keys(schema).length === 0
    }

    parseParamDefinitions = ({ schema, items = [], fieldNameKey, unwrapResponseEnvelope = false, defaultDescription = '' }) => {
        const normalizedItems = Array.isArray(items) ? items : []
        if (normalizedItems.length > 0) {
            const parsedItems = normalizedItems.map(item => ({
                [fieldNameKey]: item?.[fieldNameKey] || item?.name || '',
                paramType: item?.type || item?.paramType || 'string',
                description: item?.description || defaultDescription,
                required: !!item?.required,
                location: item?.location || 'body'
            })).filter(item => item[fieldNameKey])
            return {
                fields: parsedItems,
                exampleObject: {},
                isEmptyObjectSchema: false
            }
        }

        const normalizedSchema = this.safeParseJson(schema)
        if (!normalizedSchema) {
            return {
                fields: [],
                exampleObject: {},
                isEmptyObjectSchema: false
            }
        }

        return {
            fields: this.buildFieldsFromSchema(normalizedSchema, fieldNameKey, unwrapResponseEnvelope, defaultDescription),
            exampleObject: this.buildJsonExampleFromSchema(normalizedSchema, unwrapResponseEnvelope),
            isEmptyObjectSchema: this.isExplicitEmptyObjectSchema(normalizedSchema)
        }
    }

    extractSchemaProperties = (schema, unwrapResponseEnvelope = false) => {
        if (!schema || typeof schema !== 'object') return null
        if (unwrapResponseEnvelope && schema.properties) {
            const envelopeKey = ['value', 'data', 'result'].find(key => schema.properties[key])
            if (envelopeKey) {
                const node = schema.properties[envelopeKey] || {}
                if (node.properties && typeof node.properties === 'object') {
                    return node.properties
                }
                if (node.type === 'array' && node.items && typeof node.items.properties === 'object') {
                    return node.items.properties
                }
            }
        }
        if (schema.properties && typeof schema.properties === 'object') {
            return schema.properties
        }
        if (schema.type !== 'object' && Object.keys(schema).length > 0) {
            const fallback = {}
            Object.keys(schema).forEach(k => {
                fallback[k] = { type: typeof schema[k] }
            })
            return fallback
        }
        return null
    }
//新增一个类型归一函数
    normalizeSchemaType = (schemaNode = {}) => {
        const rawType = schemaNode?.type

        if (Array.isArray(rawType)) {
            const valid = rawType.filter(Boolean).filter(t => t !== 'null')
            if (valid.includes('array')) return 'array'
            if (valid.includes('object')) return 'object'
            if (valid.includes('integer')) return 'integer'
            if (valid.includes('number')) return 'number'
            if (valid.includes('boolean')) return 'boolean'
            return valid[0] || 'string'
        }

        if (rawType) return rawType
        if (schemaNode?.properties) return 'object'
        if (schemaNode?.items) return 'array'
        return 'string'
    }
//新增“递归解析 schema 为树”的函数
    buildTreeFieldsFromSchema = (
        schema,
        fieldNameKey = 'paramName',
        unwrapResponseEnvelope = false,
        defaultDescription = ''
    ) => {
        if (!schema || typeof schema !== 'object') return []

        const rootSchema = unwrapResponseEnvelope
            ? this.extractResponseEnvelopeSchema(schema) || schema
            : schema

        const visit = (node, options = {}) => {
            const {
                name = '',
                fullPath = '',
                parentRequiredSet = new Set(),
                location = 'body',
                level = 0
            } = options

            if (!node || typeof node !== 'object') return null

            const nodeType = this.normalizeSchemaType(node)
            const currentKey = fullPath || name || 'root'

            const current = name ? {
                key: currentKey,
                [fieldNameKey]: name,
                fullPath,
                paramType: nodeType,
                description: node?.description || defaultDescription,
                required: parentRequiredSet.has(name),
                location,
                children: []
            } : null

            // object -> 继续解析 properties
            if (nodeType === 'object' && node.properties && typeof node.properties === 'object') {
                const requiredSet = new Set(Array.isArray(node.required) ? node.required : [])
                const children = Object.keys(node.properties).map(childName => {
                    const childNode = node.properties[childName]
                    const childPath = fullPath ? `${fullPath}.${childName}` : childName
                    return visit(childNode, {
                        name: childName,
                        fullPath: childPath,
                        parentRequiredSet: requiredSet,
                        location,
                        level: level + 1
                    })
                }).filter(Boolean)

                if (current) {
                    current.children = children
                    return current
                }
                return children
            }

            // array -> 如果 items 是 object/array，继续往下挂 children
            if (nodeType === 'array') {
                const itemsNode = node.items || {}
                const itemsType = this.normalizeSchemaType(itemsNode)

                if (itemsType === 'object' && itemsNode.properties) {
                    const requiredSet = new Set(Array.isArray(itemsNode.required) ? itemsNode.required : [])
                    const children = Object.keys(itemsNode.properties).map(childName => {
                        const childSchema = itemsNode.properties[childName]
                        const childPath = fullPath ? `${fullPath}[].${childName}` : `${childName}`
                        return visit(childSchema, {
                            name: childName,
                            fullPath: childPath,
                            parentRequiredSet: requiredSet,
                            location,
                            level: level + 1
                        })
                    }).filter(Boolean)

                    if (current) {
                        current.children = children
                        return current
                    }
                }

                if (itemsType === 'array') {
                    const arrayChild = visit(itemsNode, {
                        name: '[item]',
                        fullPath: fullPath ? `${fullPath}[]` : '[item]',
                        parentRequiredSet: new Set(),
                        location,
                        level: level + 1
                    })
                    if (current && arrayChild) {
                        current.children = [arrayChild]
                    }
                }

                return current
            }

            return current
        }

        if (rootSchema.type !== 'object' && !rootSchema.properties) return []

        const rootRequiredSet = new Set(Array.isArray(rootSchema.required) ? rootSchema.required : [])
        return Object.keys(rootSchema.properties || {}).map(name => {
            const childNode = rootSchema.properties[name]
            return visit(childNode, {
                name,
                fullPath: name,
                parentRequiredSet: rootRequiredSet,
                location: 'body',
                level: 0
            })
        }).filter(Boolean)
    }
    //新增响应 envelope 提取函数
extractResponseEnvelopeSchema = (schema) => {
    if (!schema || typeof schema !== 'object' || !schema.properties) return null

    const envelopeKey = ['value', 'data', 'result'].find(key => schema.properties[key])
    if (!envelopeKey) return null

    return schema.properties[envelopeKey] || null
}
    getPlaceholderByType = (type, rawValue) => {
        if (Array.isArray(rawValue)) return []
        if (type === 'string') return ''
        if (type === 'number' || type === 'integer') return 0
        if (type === 'boolean') return false
        if (type === 'array') return []
        return {}
    }

    hasExplicitEmptyInputSchema = (selectedApi, jsonExample) => {
        if (jsonExample) {
            try {
                const parsed = typeof jsonExample === 'string' ? JSON.parse(jsonExample) : jsonExample
                if (parsed && parsed.type === 'object' && (!parsed.properties || Object.keys(parsed.properties).length === 0)) {
                    return true
                }
                if (parsed && Object.keys(parsed).length === 0) {
                    return true
                }
            } catch (e) {
                // ignore invalid json here; form validation is handled elsewhere
            }
        }
        if (!selectedApi?.requestSchema) {
            return false
        }
        try {
            const parsed = typeof selectedApi.requestSchema === 'string'
                ? JSON.parse(selectedApi.requestSchema)
                : selectedApi.requestSchema
            if (!parsed) return false
            if (parsed.type === 'object' && (!parsed.properties || Object.keys(parsed.properties).length === 0)) {
                return true
            }
            return Object.keys(parsed).length === 0
        } catch (e) {
            return false
        }
    }

    sanitizeParams = (params = []) => {
        const flatParams = this.flattenTreeParams(params)

        return flatParams
            .map(item => ({
                paramName: (item?.paramName || '').trim(),
                paramType: item?.paramType || 'string',
                description: (item?.description || '').trim(),
                required: !!item?.required,
                location: item?.location || 'body'
            }))
            .filter(item => item.paramName)
    }

    sanitizeOutputs = (outputs = []) => outputs
        .map(item => ({
            fieldName: (item?.fieldName || '').trim(),
            paramType: item?.paramType || 'string',
            description: (item?.description || '').trim()
        }))
        .filter(item => item.fieldName)

    buildResponseMappingsFromOutputs = (outputs = []) => this.sanitizeOutputs(outputs).map(item => ({
        fieldName: item.fieldName,
        jsonPath: `$.${item.fieldName}`
    }))

    areResponseMappingsEqual = (left = [], right = []) => {
        const normalize = (mappings = []) => mappings.map(item => ({
            fieldName: (item?.fieldName || '').trim(),
            jsonPath: (item?.jsonPath || '').trim()
        })).filter(item => item.fieldName || item.jsonPath)

        return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right))
    }

    autoParseJson = () => {
        const raw = this.formRef.current?.getFieldValue('jsonExample')

        if (!raw || !String(raw).trim()) {
            message.warning('请先贴入 JSON Schema')
            return
        }

        const schema = this.safeParseJson(raw, 'Failed to parse input JSON Schema')
        if (!schema) {
            message.error('JSON Schema 格式不合法')
            return
        }

        if (schema.type !== 'object' && !schema.properties) {
            message.error('仅支持 object 类型的 JSON Schema')
            return
        }

        const params = this.buildTreeFieldsFromSchema(schema, 'paramName', false, '自动解析参数')

        this.setState({
            parsedParams: params,
            inputSchemaEmptyObject: this.isExplicitEmptyObjectSchema(schema)
        })

        message.success(params.length ? '已根据 JSON Schema 生成树形参数表' : '该 Schema 无输入参数')
    }
//新增树形数据的更新函数

updateTreeNode = (nodes = [], targetKey, patch) => {
    return nodes.map(node => {
        if (node.key === targetKey) {
            return { ...node, ...patch }
        }
        if (Array.isArray(node.children) && node.children.length > 0) {
            return {
                ...node,
                children: this.updateTreeNode(node.children, targetKey, patch)
            }
        }
        return node
    })
}
//删除节点
removeTreeNode = (nodes = [], targetKey) => {
    return nodes
        .filter(node => node.key !== targetKey)
        .map(node => ({
            ...node,
            children: Array.isArray(node.children)
                ? this.removeTreeNode(node.children, targetKey)
                : []
        }))
}
    handleParamChange = (rowKey, field, value) => {
    this.setState(prev => ({
        parsedParams: this.updateTreeNode(prev.parsedParams, rowKey, { [field]: value })
    }))
}

    removeParam = (rowKey) => {
    this.setState(prev => ({
        parsedParams: this.removeTreeNode(prev.parsedParams, rowKey)
    }))
}
//新增 flatten
flattenTreeParams = (nodes = []) => {
    const result = []

    const walk = (items) => {
        items.forEach(item => {
            result.push({
                paramName: item.fullPath || item.paramName || '',
                paramType: item.paramType || 'string',
                description: item.description || '',
                required: !!item.required,
                location: item.location || 'body'
            })
            if (Array.isArray(item.children) && item.children.length > 0) {
                walk(item.children)
            }
        })
    }

    walk(nodes)
    return result
}

    addEmptyParam = () => {
        const { parsedParams } = this.state
        this.setState({ parsedParams: [...parsedParams, { paramName: '', paramType: 'string', description: '', required: false, location: 'body' }] })
    }

    handleOutputChange = (index, field, value) => {
        const { parsedOutputs } = this.state
        const newOutputs = [...parsedOutputs]
        newOutputs[index][field] = value
        this.setState({ parsedOutputs: newOutputs })
    }

    removeOutput = (index) => {
        const { parsedOutputs } = this.state
        const newOutputs = [...parsedOutputs]
        newOutputs.splice(index, 1)
        this.setState({ parsedOutputs: newOutputs })
    }

    addEmptyOutput = () => {
        const { parsedOutputs } = this.state
        this.setState({ parsedOutputs: [...parsedOutputs, { fieldName: '', paramType: 'string', description: '' }] })
    }

    handleFixedHeaderChange = (index, field, value) => {
        const fixedHeaders = [...this.state.fixedHeaders]
        fixedHeaders[index] = { ...(fixedHeaders[index] || {}), [field]: value }
        this.setState({ fixedHeaders })
    }

    addFixedHeader = () => {
        this.setState({ fixedHeaders: [...this.state.fixedHeaders, { headerName: '', headerValue: '' }] })
    }

    removeFixedHeader = (index) => {
        const fixedHeaders = [...this.state.fixedHeaders]
        fixedHeaders.splice(index, 1)
        this.setState({ fixedHeaders })
    }

    shouldShowFixedHeaders = () => {
        return (this.state.fixedHeaders || []).length > 0
    }

    handleExampleChange = (index, field, value) => {
        const examples = [...this.state.examples]
        examples[index] = { ...(examples[index] || {}), [field]: value }
        this.setState({ examples })
    }

    addExample = () => {
        this.setState({ examples: [...this.state.examples, { exampleName: '', userQuery: '', inputJson: '', outputJson: '' }] })
    }

    removeExample = (index) => {
        const examples = [...this.state.examples]
        examples.splice(index, 1)
        this.setState({ examples })
    }

    previewSpec = async () => {
        if (!this.state.editRecord?.id) {
            message.warning('请先保存当前工具，再预览发布后的 spec')
            return
        }
        this.setState({ specPreviewVisible: true, specPreviewLoading: true, specPreviewData: null })
        try {
            const versionRes = await getMcpToolVersionList({ toolId: this.state.editRecord.id })
            const versions = this.normalizeVersionList(versionRes)
            if (!versions || !versions.length) {
                message.warning('当前工具还没有版本，无法预览 spec')
                this.setState({ specPreviewLoading: false })
                return
            }
            const spec = await getMcpToolPublishedSpec({ versionId: versions[0].id || versions[0].versionId })
            this.setState({ specPreviewData: spec, specPreviewLoading: false })
        } catch (error) {
            message.error('加载 spec 预览失败')
            this.setState({ specPreviewLoading: false })
        }
    }

    // 输出参数 JSON 模板解析
    autoParseOutputJson = () => {
        const jsonStr = this.formRef.current?.getFieldValue('jsonOutputExample')
        if (!jsonStr) {
            message.warning('请先贴入 JSON 响应示例')
            return
        }
        try {
            const raw = JSON.parse(jsonStr)
            const obj = raw && typeof raw === 'object'
                ? (raw.value && typeof raw.value === 'object' && !Array.isArray(raw.value) ? raw.value
                    : raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data) ? raw.data
                        : raw.result && typeof raw.result === 'object' && !Array.isArray(raw.result) ? raw.result
                            : raw)
                : raw
            const mapType = (val) => {
                if (Array.isArray(val)) return 'array'
                if (val === null) return 'string'
                if (typeof val === 'number') return Number.isInteger(val) ? 'integer' : 'number'
                if (typeof val === 'object') return 'object'
                return typeof val
            }
            const outputs = Object.keys(obj).map(k => ({
                fieldName: k,
                paramType: mapType(obj[k]),
                description: '自动解析字段'
            }))
            this.setState({ parsedOutputs: outputs })
            message.success('已自动解析提取输出字段')
            if (this.formRef.current) {
                this.formRef.current.setFieldsValue({ jsonOutputExample: '' })
            }
        } catch (e) {
            message.error('JSON格式不合法')
        }
    }

    // 测试工具调用
    handleTestTool = async (record) => {
        if (!record || !record.id) {
            message.warning('请先保存工具后再测试')
            return
        }
        this.setState({ testing: true, testResult: null, testModalVisible: true })
        try {
            const versionRes = await getMcpToolVersionList({ toolId: record.id })
            const versions = this.normalizeVersionList(versionRes)
            if (!versions || versions.length === 0) {
                this.setState({ testResult: { success: false, message: '该工具暂无可用版本，请先保存发布' }, testing: false })
                return
            }
            const versionId = versions[0].id || versions[0].versionId
            // 先验证
            try {
                await validateMcpToolVersion({ versionId })
            } catch (e) {
                this.setState({ testResult: { success: false, message: '验证失败: ' + (e?.message || JSON.stringify(e)) }, testing: false })
                return
            }
            // 再测试
            const result = await testMcpToolVersion({ versionId })
            this.setState({ testResult: { success: true, data: result, message: '测试调用成功' }, testing: false })
        } catch (error) {
            this.setState({ testResult: { success: false, message: '测试失败: ' + (error?.message || JSON.stringify(error)) }, testing: false })
        }
    }

    // 复制文本到剪贴板
    copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => message.success('已复制到剪贴板')).catch(() => message.error('复制失败'))
    }

    buildFallbackFieldDescription = (fieldName, prefix) => {
        if (!fieldName) return prefix
        return `${prefix}${fieldName}`
    }

    ensureFieldDescriptions = (items = [], fieldKey, prefix) => items
        .map(item => ({
            ...item,
            description: (item?.description || '').trim() || this.buildFallbackFieldDescription(item?.[fieldKey], prefix)
        }))

    buildDraftDescription = (values, selectedApi) => {
        const current = (values.description || '').trim()
        if (current) return current
        const targetName = (values.name || '').trim()
            || (selectedApi?.apiName || '').trim()
            || (values.toolCode || '').trim()
            || '当前工具'
        return `${targetName}。当用户触发对应业务场景时调用该工具，按配置好的输入参数请求接口，并返回结构化结果字段供下游系统直接消费。`
    }

    getEffectiveParamAndOutputDraft = (values, selectedApi, parsedParams, parsedOutputs) => {
        let effectiveParams = this.sanitizeParams(parsedParams)
        let effectiveOutputs = this.sanitizeOutputs(parsedOutputs)

        if ((values.apiMode || 'managed') === 'bind_existing') {
            if (!effectiveParams.length && selectedApi?.requestSchema) {
                effectiveParams = this.sanitizeParams(
                    this.parseParamDefinitions({
                        schema: selectedApi.requestSchema,
                        fieldNameKey: 'paramName',
                        defaultDescription: '输入参数：'
                    }).fields
                )
            }
            if (!effectiveOutputs.length && selectedApi?.responseSchema) {
                effectiveOutputs = this.sanitizeOutputs(
                    this.parseParamDefinitions({
                        schema: selectedApi.responseSchema,
                        fieldNameKey: 'fieldName',
                        unwrapResponseEnvelope: true,
                        defaultDescription: '返回字段：'
                    }).fields
                )
            }
        }

        return {
            effectiveParams: this.ensureFieldDescriptions(effectiveParams, 'paramName', '输入参数：'),
            effectiveOutputs: this.ensureFieldDescriptions(effectiveOutputs, 'fieldName', '返回字段：')
        }
    }

    buildReadinessChecklist = (values = {}) => {
        const { parsedParams, parsedOutputs, apiDataSource } = this.state
        const selectedApi = apiDataSource.find(api => api.apiCode === values.apiCode)
        const { effectiveParams, effectiveOutputs } = this.getEffectiveParamAndOutputDraft(values, selectedApi, parsedParams, parsedOutputs)
        const issues = []
        const warnings = []
        const responseMappings = this.buildResponseMappingsFromOutputs(effectiveOutputs)
        const description = this.buildDraftDescription(values, selectedApi)

        if (!(values.name || '').trim()) issues.push('工具名称未填写')
        if (!(values.toolCode || '').trim()) issues.push('工具编码未填写')
        if (!description || description.trim().length < 10) issues.push('工具描述过短，需要说明用途、触发场景和返回结果')

        if ((values.apiMode || 'managed') === 'bind_existing') {
            if (!selectedApi) issues.push('还没有绑定 API')
        } else {
            if (!(values.method || '').trim()) issues.push('HTTP 方法未填写')
            if (!(values.url || '').trim()) issues.push('完整 API URL 未填写')
        }

        if (!effectiveOutputs.length) issues.push('至少需要 1 个输出字段，供下游理解返回结构')
        if (effectiveOutputs.some(item => !(item.description || '').trim())) issues.push('存在未填写说明的输出字段')
        if (!effectiveParams.length && !this.hasExplicitEmptyInputSchema(selectedApi, values.jsonExample)) {
            warnings.push('当前未识别到输入参数；如果这是无参接口可以忽略')
        }
        if (!responseMappings.length && effectiveOutputs.length) {
            warnings.push('建议补充 responseMapping，方便下游直接理解返回字段来源')
        }
        return { issues, warnings }
    }

    handleSaveAndLive = async (values) => {
        this.setState({ saving: true })
        try {
            const { parsedParams, parsedOutputs, editRecord, apiDataSource } = this.state
            const apiMode = values.apiMode || 'managed'
            const selectedApi = apiDataSource.find(api => api.apiCode === values.apiCode)
            const description = this.buildDraftDescription(values, selectedApi)
            const { effectiveParams, effectiveOutputs } = this.getEffectiveParamAndOutputDraft(values, selectedApi, parsedParams, parsedOutputs)
            const sanitizedParams = effectiveParams
            const sanitizedOutputs = effectiveOutputs
            const responseMappings = this.buildResponseMappingsFromOutputs(sanitizedOutputs)
            const examples = (this.state.examples || []).filter(item =>
                (item?.exampleName || '').trim() || (item?.userQuery || '').trim() || (item?.inputJson || '').trim() || (item?.outputJson || '').trim()
            )
            const hasRequestSchema = sanitizedParams.length > 0
            const hasResponseSchema = sanitizedOutputs.length > 0
            const hasExplicitEmptyInputSchema = this.hasExplicitEmptyInputSchema(selectedApi, values.jsonExample)

            if (!description) {
                message.error('工具描述（description）不能为空，它是模型理解工具用途的关键')
                return
            }

            if (apiMode === 'bind_existing') {
                if (!selectedApi) {
                    message.error('请先选择一个已注册 API')
                    return
                }
                if (!selectedApi.requestSchema || !selectedApi.responseSchema) {
                    message.error(`绑定接口 [${values.apiCode}] 尚未配置完整的 request/response schema，请先到接口库补齐`)
                    return
                }
                if ((!hasRequestSchema && !hasExplicitEmptyInputSchema) || !hasResponseSchema) {
                    message.error('当前工具的输入/输出参数未生成完整，请重新选择接口或手动补齐请求与响应字段')
                    return
                }
            } else {
                let parsedPath = values.url
                try {
                    parsedPath = new URL(values.url).pathname + new URL(values.url).search
                } catch (e) {
                    // keep original value for fallback compare
                }
                const duplicateOfficialApi = apiDataSource.find(api => {
                    return api.sourceSystem !== 'McpToolAuto'
                        && (api.method || '').toUpperCase() === values.method.toUpperCase()
                        && (api.path || '') === parsedPath
                })
                if (duplicateOfficialApi) {
                    message.error(`检测到同路由正式接口 [${duplicateOfficialApi.apiCode}]，请改用“绑定已有 API”模式`)
                    return
                }
            }

            // 1. 解析URL (例如 https://gateway.com/v1/user)
            let baseUrl = values.url
            let path = '/'
            try {
                const urlObj = new URL(values.url)
                baseUrl = urlObj.origin
                path = urlObj.pathname + urlObj.search
            } catch(e) {
                // Ignore, fallback
            }

            // 2. 构造聚合请求体
            const payload = {
                serviceId: this.getServiceId(),
                apiMode,
                toolCode: values.toolCode,
                name: values.name,
                description,
                category: values.category || '其他',
                authRequired: !!values.authRequired,
                userContext: !!values.userContext,
                maxRows: values.maxRows || 100,
                fixedHeaders: (this.state.fixedHeaders || []).filter(item => (item?.headerName || '').trim()).map(item => ({
                    headerName: (item.headerName || '').trim(),
                    headerValue: item.headerValue || ''
                })),
                responseMapping: JSON.stringify(responseMappings.reduce((acc, item) => {
                    acc[item.fieldName] = item.jsonPath
                    return acc
                }, {}), null, 2),
                examples: examples.map((item, index) => ({
                    exampleName: (item.exampleName || '').trim() || `示例${index + 1}`,
                    userQuery: item.userQuery || '',
                    inputJson: item.inputJson || '{}',
                    outputJson: item.outputJson || '{}',
                    sortOrder: index + 1
                })),
                params: sanitizedParams.map(p => ({
                    paramName: p.paramName,
                    type: p.paramType || 'string',
                    description: p.description,
                    required: !!p.required,
                    location: p.location || undefined
                })),
                outputs: sanitizedOutputs.map(p => ({
                    fieldName: p.fieldName,
                    type: p.paramType || 'string',
                    description: p.description
                }))
            }
            if (apiMode === 'bind_existing') {
                payload.apiCode = values.apiCode
            } else {
                payload.method = values.method.toUpperCase()
                payload.baseUrl = baseUrl
                payload.path = path
            }

            // 3. 区分新建 vs 编辑
            if (editRecord && editRecord.id) {
                payload.id = editRecord.id
            }
            await createToolCombo(payload)

            message.success('Tool 已自动保存并在远端一键发布！')
            // 展示连接信息
            this.setState({
                drawerVisible: false,
                publishInfoVisible: true,
                publishedTool: { toolCode: values.toolCode, name: values.name }
            })
            this.loadList()

        } catch (error) {
            console.error(error)
            message.error(error?.message || '自动编排保存失败，请检查网络或联系管理员')
        } finally {
            this.setState({ saving: false })
        }
    }

    render() {
        const { dataSource, loading, saving, drawerVisible, parsedParams, apiDataSource,
            testing, testResult, testModalVisible,
            publishInfoVisible, publishedTool, serviceInfo, selectedApiCode, apiMode } = this.state

        const apiGroups = {}
        apiDataSource.forEach(api => {
            const sys = api.sourceSystem || '未分类 (Default)'
            if (!apiGroups[sys]) apiGroups[sys] = []
            apiGroups[sys].push(api)
        })

        const serviceId = this.getServiceId()
        const mcpEndpoint = serviceInfo
            ? `${window.location.origin}/lechun-mcp/mcp/${serviceInfo.serviceCode}/sse`
            : (serviceId ? `${window.location.origin}/lechun-mcp/mcp/sse` : null)
        const apiModeCards = [
            {
                key: 'bind_existing',
                title: '复用已有接口',
                description: '从接口库选择现成接口，自动带入地址、方法、参数和说明。',
                hint: '适合接口中心已经存在正式 API'
            },
            {
                key: 'managed',
                title: '新建接口并托管',
                description: '手工填写地址和方法，保存时自动为当前工具生成接口配置。',
                hint: '适合临时调试或当前工具独占接口'
            }
        ]

        const columns = [
            {
                title: '工具资产',
                key: 'tool',
                render: (_, r) => (
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 16 }}>{r.name || r.toolName}</div>
                        <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>{r.description || '暂无描述'}</div>
                        <Space style={{ marginTop: 8 }}>
                            <Tag color="blue">{r.category || '其他'}</Tag>
                            <Tag color="cyan">{r.toolCode}</Tag>
                            {r.status === 'published' ? <Tag color="green"><CheckCircleOutlined/> 一键在线</Tag> : <Tag color="default"><StopOutlined/> 停用</Tag>}
                        </Space>
                    </div>
                )
            },
            {
                title: '维护操作',
                key: 'action',
                width: 240,
                render: (_, r) => (
                    <Space size={8}>
                        <Button type="primary" ghost icon={<EditOutlined />} onClick={() => this.openEdit(r)}>设置</Button>
                        <Button icon={<BugOutlined />} onClick={() => this.handleTestTool(r)}>测试</Button>
                    </Space>
                )
            }
        ]

        return (
            <div style={{ padding: 16 }}>
                <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                    <Col>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>MCP Tool 开发者工作台</div>
                        <div style={{ color: '#666', marginTop: 4 }}>
                            快速将标准 API 封装为 AI 可调用的 MCP Tool，提供调试与多版本管理能力。
                            {serviceInfo && <Tag color="blue" style={{ marginLeft: 8 }}>{serviceInfo.serviceName || serviceInfo.serviceCode}</Tag>}
                        </div>
                    </Col>
                    <Col>
                        <Space>
                            <Button icon={<ReloadOutlined />} onClick={this.loadList}>刷新</Button>
                            <Button type="primary" size="large" icon={<PlusOutlined />} onClick={this.openCreate}>新建 Tool</Button>
                        </Space>
                    </Col>
                </Row>

                <Card bordered={false} style={{ borderRadius: 16 }}>
                    <Table
                        dataSource={dataSource}
                        columns={columns}
                        rowKey="id"
                        loading={loading}
                        pagination={{ pageSize: 20 }}
                    />
                </Card>

                {/* ======== 工具编辑 Drawer ======== */}
                <Drawer
                    title={this.state.editRecord ? <span><span role="img" aria-label="edit">⚙️</span> 编辑 MCP Tool</span> : <span><span role="img" aria-label="new">➕</span> 新建 MCP Tool</span>}
                    width={720}
                    visible={drawerVisible}
                    onClose={() => this.setState({ drawerVisible: false })}
                    footer={null}
                    destroyOnClose
                >
                    <Form ref={this.formRef} layout="vertical" onFinish={this.handleSaveAndLive}
                        onValuesChange={(_, allValues) => this.setState({ formSnapshot: allValues })}
                        onFinishFailed={() => message.warning('保存失败：请往上滑动，检查上面的必填项（红星指示）是否已填写完整！')}>

                        {/* serviceId 缺失提示 */}
                        {!serviceId && (
                            <Alert
                                type="warning"
                                showIcon
                                message="当前未关联任何服务，创建的工具可能无法被 MCP Server 暴露。建议从「MCP服务管理」进入。"
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {/* 从已有接口快速导入 */}
                        <div style={{ background: '#eef6ff', padding: 16, borderRadius: 12, marginBottom: 24, border: '1px solid #dbe7ff' }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#175ec9' }}>
                                <span role="img" aria-label="magic">✨</span> 快速从接口库构建
                            </div>
                            <Form.Item name="apiMode" initialValue="managed" style={{ marginBottom: 16 }}>
                                <div>
                                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#223046' }}>接口来源</div>
                                    <Row gutter={12}>
                                        {apiModeCards.map(card => {
                                            const selected = apiMode === card.key
                                            return (
                                                <Col span={12} key={card.key}>
                                                    <div
                                                        onClick={() => this.handleApiModeChange(card.key)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            height: '100%',
                                                            borderRadius: 12,
                                                            padding: 16,
                                                            border: selected ? '2px solid #1677ff' : '1px solid #d9e5f5',
                                                            background: selected ? '#f0f7ff' : '#fff',
                                                            boxShadow: selected ? '0 4px 14px rgba(22,119,255,0.08)' : 'none'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                            <div style={{ fontSize: 16, fontWeight: 600, color: '#1f2a3d' }}>{card.title}</div>
                                                            {selected && <Tag color="blue" style={{ marginRight: 0 }}>当前选择</Tag>}
                                                        </div>
                                                        <div style={{ color: '#4b5b76', lineHeight: 1.6, minHeight: 44 }}>{card.description}</div>
                                                        <div style={{ marginTop: 10, color: '#7a879a', fontSize: 12 }}>{card.hint}</div>
                                                    </div>
                                                </Col>
                                            )
                                        })}
                                    </Row>
                                </div>
                            </Form.Item>

                            {apiMode === 'bind_existing' && (
                                <>
                                    <Form.Item name="apiCode" label="选择一个已有接口" rules={[{ required: true, message: '请选择一个已注册 API' }]} style={{ marginBottom: 0 }}>
                                        <Select
                                            showSearch
                                            allowClear
                                            placeholder="按所属系统分类搜索接口名称或编码"
                                            onChange={this.handleApiSelect}
                                            optionFilterProp="label"
                                            size="large"
                                        >
                                            {Object.keys(apiGroups).map(sys => (
                                                <Select.OptGroup label={sys} key={sys}>
                                                    {apiGroups[sys].map(api => (
                                                        <Select.Option key={api.apiCode} value={api.apiCode} label={`${api.apiName} ${api.apiCode}`}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                <span>{api.apiName}</span>
                                                                <span style={{ color: '#999', fontSize: 12 }}>{api.apiCode}</span>
                                                            </div>
                                                        </Select.Option>
                                                    ))}
                                                </Select.OptGroup>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Alert
                                        style={{ marginTop: 12 }}
                                        type="info"
                                        showIcon
                                        message="复用已有接口"
                                        description="保存时只会关联你选择的接口，不会改动接口中心的配置。"
                                    />
                                </>
                            )}
                            {apiMode === 'managed' && (
                                <Alert
                                    style={{ marginTop: 0 }}
                                    type="info"
                                    showIcon
                                    message="新建接口并托管"
                                    description="保存时会自动为当前工具创建接口配置。若这个地址已经是正式接口，请改用“复用已有接口”。"
                                />
                            )}
                        </div>

                        {/* 1. 基础配置 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>1. 基础认知 (AI如何找到它)</div>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="toolCode" label="唯一标识代码 (英文字母+下划线)" rules={[{ required: true }]}>
                                        <Input placeholder="eg: weather_query" disabled={!!this.state.editRecord} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="name" label="工具展示名称" rules={[{ required: true }]}>
                                        <Input placeholder="eg: 全球天气查询仪" />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name="description" label="详细Prompt描述 (建议填写，不填时系统会自动补一版默认描述)">
                                <TextArea rows={3} placeholder="告诉模型这个工具是做什么的，在何种上下文中调用它。例如：当用户询问任何关于天气的状况时，调用此工具。" />
                            </Form.Item>
                            {(() => {
                                const readiness = this.buildReadinessChecklist(this.state.formSnapshot || {})
                                if (!readiness.issues.length && !readiness.warnings.length) {
                                    return (
                                        <Alert
                                            type="success"
                                            showIcon
                                            message="下游接入就绪"
                                            description="当前配置已经满足下游伙伴直接消费 spec 的最小要求。"
                                        />
                                    )
                                }
                                return (
                                    <Alert
                                        type={readiness.issues.length ? 'warning' : 'info'}
                                        showIcon
                                        message={readiness.issues.length ? '还缺少这些关键项' : '发布前提醒'}
                                        description={(
                                            <div>
                                                {readiness.issues.length > 0 && (
                                                    <ul style={{ paddingLeft: 18, marginBottom: readiness.warnings.length ? 8 : 0 }}>
                                                        {readiness.issues.map(item => <li key={item}>{item}</li>)}
                                                    </ul>
                                                )}
                                                {readiness.warnings.length > 0 && (
                                                    <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                                                        {readiness.warnings.map(item => <li key={item}>{item}</li>)}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    />
                                )
                            })()}
                        </div>

                        {/* 2. 接口直连 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>2. 接口直达 (从哪里获取果实)</div>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item name="method" label={apiMode === 'bind_existing' ? 'HTTP方式（自动带入）' : 'HTTP方式'} rules={apiMode === 'managed' ? [{ required: true }] : []}>
                                        <Select
                                            disabled={apiMode === 'bind_existing'}
                                            options={['GET','POST','PUT','DELETE'].map(x => ({label:x, value:x}))}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={18}>
                                    <Form.Item name="url" label={apiMode === 'bind_existing' ? '完整 API URL（自动带入）' : '完整 API URL'} rules={apiMode === 'managed' ? [{ required: true, type: 'url' }] : []}>
                                        <Input
                                            disabled={apiMode === 'bind_existing'}
                                            placeholder="https://api.example.com/v1/weather"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {apiMode === 'bind_existing' && selectedApiCode && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="已绑定现有 API，接口地址与请求方式以接口中心为准"
                                    description="如需改为手工填写地址，请切换到上方“新建接口并托管”。"
                                />
                            )}
                        </div>

                        {/* 3. 输入参数 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                                3. 模型输入参数 (无需繁琐填写，一键提取)
                            </div>

<Form.Item name="jsonExample" label="贴入请求 JSON Schema (可选)">
    <TextArea
        rows={8}
        placeholder={`{
  "type": "object",
  "properties": {
    "tasks": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "content": { "type": "string", "description": "任务标题" }
        }
      }
    }
  }
}`}
    />
</Form.Item>
                            {this.state.inputSchemaEmptyObject && (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message="该接口无输入参数，系统已自动带入 {}"
                                />
                            )}
                            <Button size="small" type="primary" ghost icon={<CodeOutlined />} onClick={this.autoParseJson} style={{ marginBottom: 16 }}>
                                万物识别：自动生成下方参数表
                            </Button>

                            <Table
                                size="small"
                                pagination={false}
                                dataSource={parsedParams}
                                rowKey="key"
                                childrenColumnName="children"
                                defaultExpandAllRows
                                columns={[
                                    {
                                        title: '参数名称',
                                        dataIndex: 'paramName',
                                        width: 220,
                                        render: (v, r) => (
                                            <div>
                                                <Input
                                                    size="small"
                                                    value={v}
                                                    onChange={e => this.handleParamChange(r.key, 'paramName', e.target.value)}
                                                />
                                                {r.fullPath && r.fullPath !== v && (
                                                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                                                        路径：{r.fullPath}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    },
                                    {
                                        title: '类型',
                                        dataIndex: 'paramType',
                                        width: 110,
                                        render: (v, r) => (
                                            <Select
                                                size="small"
                                                value={v}
                                                onChange={val => this.handleParamChange(r.key, 'paramType', val)}
                                                options={['string', 'number', 'integer', 'boolean', 'array', 'object'].map(x => ({
                                                    label: x,
                                                    value: x
                                                }))}
                                            />
                                        )
                                    },
                                    {
                                        title: '位置',
                                        dataIndex: 'location',
                                        width: 100,
                                        render: (v, r) => (
                                            <Select
                                                size="small"
                                                value={v || 'body'}
                                                onChange={val => this.handleParamChange(r.key, 'location', val)}
                                                options={['query', 'body', 'path', 'header'].map(x => ({
                                                    label: x,
                                                    value: x
                                                }))}
                                            />
                                        )
                                    },
                                    {
                                        title: '必填',
                                        dataIndex: 'required',
                                        width: 80,
                                        render: (v, r) => (
                                            <Switch
                                                size="small"
                                                checked={!!v}
                                                onChange={val => this.handleParamChange(r.key, 'required', val)}
                                            />
                                        )
                                    },
                                    {
                                        title: '用途描述 (给AI看)',
                                        dataIndex: 'description',
                                        render: (v, r) => (
                                            <Input
                                                size="small"
                                                value={v}
                                                onChange={e => this.handleParamChange(r.key, 'description', e.target.value)}
                                            />
                                        )
                                    },
                                    {
                                        title: '',
                                        width: 50,
                                        render: (_, r) => (
                                            <Button
                                                size="small"
                                                danger
                                                type="text"
                                                icon={<DeleteOutlined />}
                                                onClick={() => this.removeParam(r.key)}
                                            />
                                        )
                                    }
                                ]}
                            />
                            <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addEmptyParam} style={{ marginTop: 8 }}>
                                手工添加一行
                            </Button>
                        </div>

                        {/* 4. 输出参数 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                                4. 模型输出参数 (告诉AI返回哪些字段)
                            </div>

                            <Form.Item name="jsonOutputExample" label="贴入一段完整响应JSON模板 (可选)">
                                <TextArea rows={3} placeholder='{"temperature": 25, "weather": "sunny", "humidity": 60}' />
                            </Form.Item>
                            <Button size="small" type="primary" ghost icon={<CodeOutlined />} onClick={this.autoParseOutputJson} style={{ marginBottom: 16 }}>
                                万物识别：自动生成下方输出字段表
                            </Button>

                            <Table
                                size="small"
                                pagination={false}
                                dataSource={this.state.parsedOutputs}
                                rowKey={(r, idx) => idx}
                                columns={[
                                    { title: '字段名称', dataIndex: 'fieldName', width: 140, render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleOutputChange(idx, 'fieldName', e.target.value)} /> },
                                    { title: '类型', dataIndex: 'paramType', width: 100, render: (v, r, idx) => (
                                        <Select size="small" value={v} onChange={val => this.handleOutputChange(idx, 'paramType', val)} options={['string','number','integer','boolean','array','object'].map(x => ({label:x, value:x}))} />
                                    )},
                                    { title: '用途描述 (给AI看)', dataIndex: 'description', render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleOutputChange(idx, 'description', e.target.value)} /> },
                                    { title: '', width: 50, render: (_, r, idx) => <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => this.removeOutput(idx)} /> }
                                ]}
                            />
                            <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addEmptyOutput} style={{ marginTop: 8 }}>
                                手工添加一行
                            </Button>
                        </div>

                        {/* 5. 调用策略 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>5. 调用策略</div>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Form.Item name="authRequired" label="是否要求鉴权" valuePropName="checked">
                                        <Switch checkedChildren="需要" unCheckedChildren="公开" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="userContext" label="是否透传用户上下文" valuePropName="checked">
                                        <Switch checkedChildren="透传" unCheckedChildren="关闭" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="maxRows" label="最大返回行数" initialValue={100}>
                                        <InputNumber min={1} max={10000} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {this.shouldShowFixedHeaders() ? (
                                <>
                                    <div style={{ marginBottom: 12, fontSize: 13, color: '#4b5b76' }}>固定请求头</div>
                                    <Table
                                        size="small"
                                        pagination={false}
                                        dataSource={this.state.fixedHeaders}
                                        rowKey={(r, idx) => idx}
                                        columns={[
                                            { title: 'Header 名称', dataIndex: 'headerName', width: 220, render: (v, r, idx) => <Input size="small" value={v} placeholder="如 Authorization" onChange={e => this.handleFixedHeaderChange(idx, 'headerName', e.target.value)} /> },
                                            { title: 'Header 值', dataIndex: 'headerValue', render: (v, r, idx) => <Input size="small" value={v} placeholder="如 Bearer xxx" onChange={e => this.handleFixedHeaderChange(idx, 'headerValue', e.target.value)} /> },
                                            { title: '', width: 50, render: (_, r, idx) => <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => this.removeFixedHeader(idx)} /> }
                                        ]}
                                    />
                                    <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addFixedHeader} style={{ marginTop: 8 }}>
                                        添加固定请求头
                                    </Button>
                                </>
                            ) : (
                                <div style={{ marginTop: 8 }}>
                                    <Text type="secondary">当前工具无需固定请求头，可留空。</Text>
                                    <Button type="link" icon={<PlusOutlined />} onClick={this.addFixedHeader} style={{ paddingLeft: 8 }}>
                                        需要时再添加固定请求头
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* 6. 响应映射 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>6. 响应映射 (把接口响应字段映射到最终 spec)</div>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                                输出字段生成后，这里会自动带出标准映射，无需人工填写。
                            </Text>
                            <Table
                                size="small"
                                pagination={false}
                                dataSource={this.state.responseMappings}
                                rowKey={(r, idx) => idx}
                                locale={{ emptyText: '填写上方输出字段后，这里会自动生成映射' }}
                                columns={[
                                    { title: '输出字段', dataIndex: 'fieldName', width: 180 },
                                    { title: 'JSONPath', dataIndex: 'jsonPath', render: v => <Text code>{v}</Text> }
                                ]}
                            />
                        </div>

                        {/* 7. 示例 */}
                        <div style={{ background: '#f8fafc', padding: 16, borderRadius: 12, marginBottom: 24 }}>
                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>7. 调用示例</div>
                            <Table
                                size="small"
                                pagination={false}
                                dataSource={this.state.examples}
                                rowKey={(r, idx) => idx}
                                locale={{ emptyText: '暂无数据' }}
                                columns={[
                                    { title: '示例名', dataIndex: 'exampleName', width: 160, render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleExampleChange(idx, 'exampleName', e.target.value)} /> },
                                    { title: '用户问题', dataIndex: 'userQuery', width: 200, render: (v, r, idx) => <Input size="small" value={v} onChange={e => this.handleExampleChange(idx, 'userQuery', e.target.value)} /> },
                                    { title: '输入 JSON', dataIndex: 'inputJson', render: (v, r, idx) => <TextArea rows={3} value={v} onChange={e => this.handleExampleChange(idx, 'inputJson', e.target.value)} /> },
                                    { title: '输出 JSON', dataIndex: 'outputJson', render: (v, r, idx) => <TextArea rows={3} value={v} onChange={e => this.handleExampleChange(idx, 'outputJson', e.target.value)} /> },
                                    { title: '', width: 50, render: (_, r, idx) => <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => this.removeExample(idx)} /> }
                                ]}
                            />
                            <Button type="dashed" block size="small" icon={<PlusOutlined />} onClick={this.addExample} style={{ marginTop: 8 }}>
                                添加示例
                            </Button>
                        </div>

                        {/* 底部按钮区 */}
                        <Space direction="vertical" style={{ width: '100%' }} size={12}>
                            {this.state.editRecord && (
                                <Button size="large" block icon={<CodeOutlined/>} onClick={this.previewSpec}>
                                    预览当前版本 Spec
                                </Button>
                            )}
                            {this.state.editRecord && (
                                <Button size="large" block icon={<BugOutlined/>}
                                    loading={testing}
                                    onClick={() => this.handleTestTool(this.state.editRecord)}>
                                    先测试一下，看看能不能通
                                </Button>
                            )}
                            <Button type="primary" size="large" htmlType="submit" loading={saving} block icon={<ThunderboltOutlined/>}>
                                {this.state.editRecord
                                    ? (apiMode === 'bind_existing' ? '保存并重新绑定发布' : '保存并重新发布托管接口')
                                    : (apiMode === 'bind_existing' ? '保存并发布到当前服务' : '保存并创建托管接口')}
                            </Button>
                        </Space>
                    </Form>
                </Drawer>

                {/* ======== 测试结果弹窗 ======== */}
                <Modal
                    title={<span><BugOutlined /> 工具测试结果</span>}
                    visible={testModalVisible}
                    onCancel={() => this.setState({ testModalVisible: false, testResult: null })}
                    footer={<Button onClick={() => this.setState({ testModalVisible: false })}>关闭</Button>}
                    width={600}
                >
                    {testing && <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="正在调用后端测试..." /></div>}
                    {testResult && !testing && (
                        <div>
                            <Alert
                                type={testResult.success ? 'success' : 'error'}
                                showIcon
                                message={testResult.message}
                                style={{ marginBottom: 16 }}
                            />
                            {testResult.data && (
                                <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, maxHeight: 300, overflow: 'auto' }}>
                                    <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(testResult.data, null, 2)}</pre>
                                </div>
                            )}
                        </div>
                    )}
                </Modal>

                <Modal
                    title={<span><CodeOutlined /> Spec 预览</span>}
                    visible={this.state.specPreviewVisible}
                    onCancel={() => this.setState({ specPreviewVisible: false })}
                    footer={<Button onClick={() => this.setState({ specPreviewVisible: false })}>关闭</Button>}
                    width={860}
                >
                    {this.state.specPreviewLoading
                        ? <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="正在加载 spec..." /></div>
                        : (
                            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, maxHeight: 520, overflow: 'auto' }}>
                                <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify(this.state.specPreviewData, null, 2)}</pre>
                            </div>
                        )}
                </Modal>

                {/* ======== 发布成功 - 连接信息弹窗 ======== */}
                <Modal
                    title={<span><CheckCircleOutlined style={{ color: '#52c41a' }} /> 发布成功 - MCP 接入指南</span>}
                    visible={publishInfoVisible}
                    onCancel={() => this.setState({ publishInfoVisible: false })}
                    footer={<Button type="primary" onClick={() => this.setState({ publishInfoVisible: false })}>知道了</Button>}
                    width={640}
                >
                    {publishedTool && (
                        <div>
                            <Alert type="success" showIcon message={`工具「${publishedTool.name}」已成功发布上线！`} style={{ marginBottom: 16 }} />

                            <div style={{ fontWeight: 600, marginBottom: 8 }}>MCP Server SSE Endpoint</div>
                            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <code style={{ fontSize: 13, wordBreak: 'break-all' }}>{mcpEndpoint || '请从服务管理页进入以获取完整地址'}</code>
                                {mcpEndpoint && <Button size="small" icon={<CopyOutlined />} onClick={() => this.copyToClipboard(mcpEndpoint)}>复制</Button>}
                            </div>

                            <div style={{ fontWeight: 600, marginBottom: 8 }}>客户端配置示例 (Claude Desktop / Cursor)</div>
                            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 8, position: 'relative' }}>
                                <pre style={{ margin: 0, fontSize: 12 }}>{JSON.stringify({
                                    mcpServers: {
                                        [serviceInfo?.serviceCode || 'my-service']: {
                                            url: mcpEndpoint || '<MCP_SSE_ENDPOINT>',
                                            headers: {
                                                'X-API-Key': '<your-api-key>'
                                            }
                                        }
                                    }
                                }, null, 2)}</pre>
                                <Button size="small" icon={<CopyOutlined />}
                                    style={{ position: 'absolute', top: 8, right: 8 }}
                                    onClick={() => this.copyToClipboard(JSON.stringify({
                                        mcpServers: {
                                            [serviceInfo?.serviceCode || 'my-service']: {
                                                url: mcpEndpoint || '<MCP_SSE_ENDPOINT>',
                                                headers: { 'X-API-Key': '<your-api-key>' }
                                            }
                                        }
                                    }, null, 2))}>复制</Button>
                            </div>

                            <Alert type="info" showIcon style={{ marginTop: 16 }}
                                message="API Key 可在「API Key 管理」页面生成和管理"
                            />
                        </div>
                    )}
                </Modal>
            </div>
        )
    }
}
